import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { openExternal } from "@/constants/links";
import useFeatures from "@/constants/useFeatures";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_EMOJIS = {
  "Cere e Pomate": "💈",
  "Tagliacapelli Pro": "✂️",
  "Kit Cura Barba": "🧔",
  "Forbici & Strumenti": "🔪",
  "Mantelline da Salone": "🧥",
  "Carta Collo & Igiene": "🧻",
  "Talco da Barbiere": "🌟",
  "Grooming Premium": "👑",
  "Gel per Capelli": "💧",
  "Shampoo Uomo": "🫧",
  Dopobarba: "🌿",
  "Rasoi & Lame": "🪒",
};

const POS_KEY = "bl_shop_pos";

function loadPos() {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY));
    if (p && typeof p.x === "number" && typeof p.y === "number") return p;
  } catch {}
  return { x: 0, y: 0 };
}

function clampPos(x, y) {
  // Base: fixed bottom-24 right-4. Permite arrastrarla por toda la pantalla
  // sin sacarla de los bordes visibles.
  const minX = -(window.innerWidth - 140);
  const minY = -(window.innerHeight - 170);
  return {
    x: Math.max(minX, Math.min(8, x)),
    y: Math.max(minY, Math.min(48, y)),
  };
}

// Nube flotante de ventas: visible SOLO si el dueño guardó links de afiliado
// en el área reservada. Abre un panel con esos productos que llevan directo a Amazon.
// ARRASTRABLE: mantén pulsada y muévela donde no tape; la posición se guarda.
export default function AffiliateCloud() {
  const features = useFeatures();
  const [links, setLinks] = useState({});
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(loadPos);

  useEffect(() => {
    fetch(`${BACKEND}/api/product-links`)
      .then((r) => r.json())
      .then((d) => setLinks(d || {}))
      .catch(() => {});
  }, []);

  const items = Object.entries(links);
  if (items.length === 0) return null; // sin productos seleccionados -> la nube se esconde
  if (!features.shop) return null; // función apagada desde "Funzioni dell'app"

  const emojiFor = (name) => PRODUCT_EMOJIS[name] || "🛍️";

  return (
    <>
      <motion.div
        data-testid="affiliate-cloud-btn"
        drag
        dragMomentum={false}
        dragElastic={0.05}
        initial={{ x: pos.x, y: pos.y }}
        animate={{ x: pos.x, y: pos.y }}
        transition={{ duration: 0.15 }}
        onTap={() => setOpen(true)}
        onDragEnd={(e, info) => {
          const p = clampPos(pos.x + info.offset.x, pos.y + info.offset.y);
          setPos(p);
          localStorage.setItem(POS_KEY, JSON.stringify(p));
        }}
        className="fixed bottom-24 right-4 z-[62] cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}
      >
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 16, delay: 2.5 }}
          whileTap={{ scale: 0.92 }}
          className="flex items-center gap-2 rounded-full border border-amber-400/50 bg-zinc-900/95 py-2.5 pl-3 pr-4 shadow-[0_4px_24px_rgba(251,191,36,0.35)] backdrop-blur"
        >
          <span className="relative text-lg leading-none">
            🛍️
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-0.5 text-[9px] font-extrabold text-zinc-950">
              {items.length}
            </span>
          </span>
          <span className="text-[11px] font-extrabold uppercase tracking-widest text-amber-300">
            Shop
          </span>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[65] bg-black/60"
            />
            <motion.div
              data-testid="affiliate-cloud-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="fixed inset-x-0 bottom-0 z-[70] mx-auto w-full max-w-md rounded-t-3xl border-t border-amber-400/30 bg-zinc-900 p-5 pb-10 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]"
            >
              <div className="mb-1 flex items-center justify-between">
                <p className="text-sm font-extrabold uppercase tracking-widest text-amber-300">
                  ✨ I prodotti che uso in salone
                </p>
                <button
                  data-testid="affiliate-cloud-close"
                  onClick={() => setOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:text-white"
                >
                  <X size={15} />
                </button>
              </div>
              <p className="mb-4 text-xs leading-relaxed text-zinc-500">
                Scelti da me per te — li compri direttamente su Amazon. 🚀
              </p>
              <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto pr-1">
                {items.map(([name, url], i) => (
                  <button
                    key={name}
                    data-testid={`affiliate-cloud-item-${i}`}
                    onClick={openExternal(url)}
                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-zinc-950/70 p-3 text-left transition-colors duration-200 hover:border-amber-400/40 active:scale-[0.98]"
                  >
                    <span className="text-2xl">{emojiFor(name)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold text-white">{name}</span>
                      <span className="block text-[11px] font-semibold text-amber-300">
                        Compra su Amazon →
                      </span>
                    </span>
                    <span className="text-base">🛒</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
